import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ValidationModal } from "@/components/ui/ValidationModal";
import { useForm, Controller, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, AlertCircle, Building2, MapPin, BadgeCheck, CalendarClock, Map, ShieldCheck } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { companyAddSchema, getFilterValueAngularStyle, stripPhoneDigitsForApi } from "../utils/companyValidators";
import type { CompanyAddFormValues, DuplicateCompanyRow } from "../types";
import { createCompany, getCompanyConfig } from "../services/companyService";
import { ensureReferenceDataLoaded } from "@/lib/api/referenceDataService";
import { getStates, type State } from "@/lib/api/statesService";
import { formatPhoneNumber, cleanPhoneNumber } from "@/features/invitee/utils/inviteeFormatters";
import { showSuccess, showError, formatApiError } from "@/lib/utils/notifications";
import { collectNestedErrorMessages } from "@/features/config/utils/formErrors";
import { parseDuplicateCompanyAddressFromApiError } from "../utils/companyDuplicateAddress";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageHeader } from "@/features/invitee/components/PageHeader";
import { geocodeAddress } from "@/lib/utils/geocoding";
import { loadGoogleMapsScript } from "@/lib/utils/googleMaps";
import { cn } from "@/lib/utils/helpers";
import { SectionCard } from "@/components/ui/SectionCard";
import { companyEditConfirmPresentation } from "../constants";

import styles from "../styles/companyAdd.module.css";
import tabStyles from "../styles/companyInfoTab.module.css";

export function CompanyAdd() {
    const navigate = useNavigate();
    const { isLibertyRole } = useUserRole();

    const [states, setStates] = useState<State[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationModalMessage, setValidationModalMessage] = useState('');
    const [validationModalVariant, setValidationModalVariant] = useState<'error' | 'success'>('error');
    const [validationModalTitle, setValidationModalTitle] = useState<string | undefined>(undefined);

    // Dictionaries for dropdowns
    const [propertyMgmtSystems, setPropertyMgmtSystems] = useState<{ label: string, value: string }[]>([]);
    const [residentScreenings, setResidentScreenings] = useState<{ label: string, value: string }[]>([]);

    // Map verification states
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<google.maps.Map | null>(null);
    const markerInstance = useRef<google.maps.Marker | null>(null);
    const [isValidatingAddress, setIsValidatingAddress] = useState(false);
    const [isAddressManuallyChanged, setIsAddressManuallyChanged] = useState(true);
    const [duplicateAddressConfirmOpen, setDuplicateAddressConfirmOpen] = useState(false);
    const [duplicateCompanies, setDuplicateCompanies] = useState<DuplicateCompanyRow[]>([]);
    const pendingAddFormRef = useRef<CompanyAddFormValues | null>(null);

    const {
        register,
        
        control,
        watch,
        setValue,
        trigger,
        handleSubmit,
        formState: { errors },
    } = useForm<CompanyAddFormValues>({
        resolver: zodResolver(companyAddSchema),
        defaultValues: {
            company_name: "",
            office_phone: "",
            contact_name: "",
            contact_mobile: "",
            address1: "",
            address2: "",
            city: "",
            state: "",
            zipcode: "",
            listed: "1",
            email_frequency: "MONTHLY",
            property_mgmt_system: [],
            property_mgmt_system_other: "",
            resident_screening: [],
            resident_screening_other: "",
            latitude: "",
            longitude: "",
        },
    });

    const watchPropertyMgmt = watch("property_mgmt_system") || [];
    const watchResidentScreening = watch("resident_screening") || [];
    const watchLatitude = watch("latitude");
    const watchLongitude = watch("longitude");
    // Initialize Map side-effect
    useEffect(() => {
        const initMap = async () => {
            if (!mapRef.current) return;

            // Default fallback coordinates (matches Angular: 30.5557898, -87.8939053)
            const lat = Number(watchLatitude) || 30.5557898;
            const lng = Number(watchLongitude) || -87.8939053;

            try {
                await loadGoogleMapsScript();
                if (window.google?.maps) {
                    if (!mapInstance.current) {
                        mapInstance.current = new window.google.maps.Map(mapRef.current, {
                            center: { lat, lng },
                            zoom: 14,
                        });
                        markerInstance.current = new window.google.maps.Marker({
                            position: { lat, lng },
                            map: mapInstance.current,
                        });
                    } else if (mapInstance.current && markerInstance.current) {
                        mapInstance.current.setCenter({ lat, lng });
                        markerInstance.current.setPosition({ lat, lng });
                    }
                }
            } catch (err) {
                console.error("Failed to load Google Maps API for rendering", err);
            }
        };

        initMap();
    }, [watchLatitude, watchLongitude]);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                await ensureReferenceDataLoaded();
                const [fetchedStates, configResponse] = await Promise.all([getStates(), getCompanyConfig()]);

                setStates(fetchedStates);

                // Process Config Options
                if (configResponse) {
                    const propSystems = configResponse.property_mgmt_system || [];
                    const resScreening = configResponse.resident_screening || [];
                    setPropertyMgmtSystems([
                        ...propSystems.map((t: string) => ({ label: t, value: t })),
                        { label: 'Other', value: 'Other' }
                    ]);
                    setResidentScreenings([
                        ...resScreening.map((t: string) => ({ label: t, value: t })),
                        { label: 'Other', value: 'Other' }
                    ]);
                }

            } catch (error) {
                console.error("Failed to fetch reference data:", error);
                showError("Failed to initialize some form options.");
            }
        };
        fetchInitialData();
    }, []);

    // Handle form change to require re-validation
    useEffect(() => {
        const subscription = watch((_, { name }) => {
            if (name === 'address1' || name === 'address2' || name === 'city' || name === 'state' || name === 'zipcode') {
                setIsAddressManuallyChanged(true);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const handleValidateAddress = async () => {
        const isValid = await trigger(["address1", "city", "state", "zipcode"]);
        if (!isValid) return;

        const data = watch();

        setIsValidatingAddress(true);

        let fullAddress = data.address1;
        if (data.address2) fullAddress += ' ' + data.address2;
        fullAddress += ` ${data.city} ${data.state} ${data.zipcode}`;

        const addressVerify = `Warning: Unable to verify address. If you know this address is accurate or is not yet in Google Maps, you may still continue and save this record. Try verifying the address again if the map location is incorrect.`;
        try {
            const result = await geocodeAddress(fullAddress);
            if (result && !result.partial_match) {
                setValue("latitude", result.lat, { shouldValidate: true });
                setValue("longitude", result.lng, { shouldValidate: true });

                let zipValid = false;
                let stateValid = false;
                if (result.address_components.postal_code === data.zipcode) {
                    zipValid = true;
                }
                if (result.address_components.administrative_area_level_1 === data.state) {
                    stateValid = true;
                }

                if (!zipValid || !stateValid) {
                    setValidationModalVariant('error');
                    setValidationModalTitle(undefined);
                    setValidationModalMessage(addressVerify);
                    setShowValidationModal(true);
                    setIsAddressManuallyChanged(false);
                    return;
                }

                setValidationModalVariant('success');
                setValidationModalTitle(undefined);
                setValidationModalMessage(`Address verified with Latitude: ${result.lat} Longitude: ${result.lng}`);
                setShowValidationModal(true);
                setIsAddressManuallyChanged(false);
            } else {
                setValidationModalVariant('error');
                setValidationModalTitle(undefined);
                setValidationModalMessage(addressVerify);
                setShowValidationModal(true);
                setIsAddressManuallyChanged(false);
            }
        } catch {
            setValidationModalVariant('error');
            setValidationModalTitle(undefined);
            setValidationModalMessage(addressVerify);
            setShowValidationModal(true);
            setIsAddressManuallyChanged(false);
        } finally {
            setIsValidatingAddress(false);
        }
    };

    const buildCompanyAddFormData = (data: CompanyAddFormValues, includeCheckExist: boolean): FormData => {
        const formData = new FormData();

        const scalarPayload: Record<string, unknown> = {
            company_name: data.company_name,
            contact_name: data.contact_name,
            company_phone: stripPhoneDigitsForApi(data.contact_mobile),
            company_office_phone: stripPhoneDigitsForApi(data.office_phone),
            company_address: data.address1,
            company_address2: data.address2,
            company_state: data.state,
            company_city: data.city,
            company_zipcode: data.zipcode,
            listed: data.listed,
            email_frequency: data.email_frequency,
            pmc_invite: "1",
        };
        if (data.latitude) scalarPayload.latitude = data.latitude;
        if (data.longitude) scalarPayload.longitude = data.longitude;
        if (data.property_mgmt_system_other && data.property_mgmt_system?.includes('Other')) {
            scalarPayload.property_mgmt_system_other = data.property_mgmt_system_other;
        }
        if (data.resident_screening_other && data.resident_screening?.includes('Other')) {
            scalarPayload.resident_screening_other = data.resident_screening_other;
        }

        const filtered = getFilterValueAngularStyle(scalarPayload);
        for (const [key, value] of Object.entries(filtered)) {
            formData.append(key, String(value));
        }

        if (includeCheckExist) {
            formData.append('check_exist', 'true');
        }

        if (data.property_mgmt_system && data.property_mgmt_system.length > 0) {
            data.property_mgmt_system.forEach((pmsId, index) => {
                formData.append(`property_mgmt_system[${index}]`, String(pmsId).trim());
            });
        }
        if (data.resident_screening && data.resident_screening.length > 0) {
            data.resident_screening.forEach((rsId, index) => {
                formData.append(`resident_screening[${index}]`, String(rsId).trim());
            });
        }

        return formData;
    };

    const duplicateAddressHtmlMessage = useMemo(() => {
        if (!duplicateCompanies.length) return "";
        const links = duplicateCompanies
            .map(
                (c) =>
                    `${c.name} - <a data-testid="company-add-duplicate-company-link-${c.id}" href="/company/edit/${c.id}" target="_blank" rel="noopener noreferrer">${c.id}</a>`,
            )
            .join("<br>");
        return `${links}<br><br>If you have confirmed that there are multiple companies at the same address, then Save and continue.`;
    }, [duplicateCompanies]);

    const submitWithPayload = async (data: CompanyAddFormValues, opts?: { skipCheckExist?: boolean }) => {
        setIsSubmitting(true);
        try {
            const formData = buildCompanyAddFormData(data, !opts?.skipCheckExist);
            await createCompany(formData);
            showSuccess("Company created successfully");
            navigate('/company');
        } catch (error: unknown) {
            if (!opts?.skipCheckExist) {
                const dupes = parseDuplicateCompanyAddressFromApiError(error);
                if (dupes?.length) {
                    pendingAddFormRef.current = data;
                    setDuplicateCompanies(dupes);
                    setDuplicateAddressConfirmOpen(true);
                    return;
                }
            }
            console.error("Error creating company:", error);
            showError(formatApiError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDuplicateCompanyAddressSave = () => {
        setDuplicateAddressConfirmOpen(false);
        const data = pendingAddFormRef.current;
        pendingAddFormRef.current = null;
        if (!data) return;
        void submitWithPayload(data, { skipCheckExist: true });
    };

    const handleDuplicateCompanyAddressCancel = () => {
        setDuplicateAddressConfirmOpen(false);
        pendingAddFormRef.current = null;
    };

    const handleFormSubmit = handleSubmit(
        (data) => {
            const addressFilled = Boolean(data.address1?.trim() || data.city?.trim() || data.state?.trim() || data.zipcode?.trim());
            if (isAddressManuallyChanged && addressFilled) {
                // Show the ValidationModal popup instead of a toast
                setValidationModalVariant('error');
                setValidationModalTitle('Validate Address');
                setValidationModalMessage('Please validate address before saving.');
                setShowValidationModal(true);
                return;
            }
            submitWithPayload(data);
        },
        (errors: FieldErrors<CompanyAddFormValues>) => {
            const messages = collectNestedErrorMessages(errors);
            setValidationModalVariant('error');
            setValidationModalTitle('Missing Fields');
            setValidationModalMessage(
                messages.length > 0
                    ? messages.join('\n')
                    : 'Please fix the validation errors before submitting.',
            );
            setShowValidationModal(true);
        }
    );

    return (
        <div className="w-full min-h-screen bg-slate-50/50" data-testid="company-add-page">
            <PageHeader title="Add Company" testIdRoot="company-add-page-header" />

            <div className="mx-4 my-4 sm:mx-6 sm:my-4">
                <h1 className="mb-4 text-center text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                    Tell us about the company
                </h1>

                <form
                    onSubmit={handleFormSubmit}
                    className={cn(tabStyles.tabContent, "form_inline w-full min-w-0")}
                    data-testid="company-add-form"
                >

                    <SectionCard
                        icon={Building2}
                        title="Company Information"
                        testId="company-add-section-company-information"
                        titleTestId="company-add-section-company-information-title"
                    >
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Company Name<span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    data-testid="company-add-company-name-input"
                                    type="text"
                                    {...register("company_name")}
                                    className={`${styles.customFormControl} ${errors.company_name ? '!border-red-500' : ''}`}
                                />
                                {errors.company_name && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.company_name.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Office Phone</label>
                                <Controller
                                    name="office_phone"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            data-testid="company-add-office-phone-input"
                                            type="text"
                                            placeholder="(xxx) xxx-xxxx"
                                            {...field}
                                            value={field.value ?? ""}
                                            onChange={(e) => {
                                                const cleaned = cleanPhoneNumber(e.target.value);
                                                const formatted = formatPhoneNumber(cleaned);
                                                field.onChange(formatted);
                                            }}
                                            className={`${styles.customFormControl} ${errors.office_phone ? '!border-red-500' : ''}`}
                                        />
                                    )}
                                />
                                {errors.office_phone && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.office_phone.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Primary Contact<span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    data-testid="company-add-primary-contact-input"
                                    type="text"
                                    placeholder="Full name"
                                    {...register("contact_name")}
                                    className={`${styles.customFormControl} ${errors.contact_name ? '!border-red-500' : ''}`}
                                />
                                {errors.contact_name && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.contact_name.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Primary Phone<span className={styles.requiredStar}>*</span>
                                </label>
                                <Controller
                                    name="contact_mobile"
                                    control={control}
                                    render={({ field }) => (
                                        <input
                                            data-testid="company-add-primary-phone-input"
                                            type="text"
                                            placeholder="(xxx) xxx-xxxx"
                                            {...field}
                                            value={field.value ?? ""}
                                            onChange={(e) => {
                                                const cleaned = cleanPhoneNumber(e.target.value);
                                                const formatted = formatPhoneNumber(cleaned);
                                                field.onChange(formatted);
                                            }}
                                            className={`${styles.customFormControl} ${errors.contact_mobile ? '!border-red-500' : ''}`}
                                        />
                                    )}
                                />
                                {errors.contact_mobile && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.contact_mobile.message}
                                    </div>
                                )}
                        </div>
                    </div>
                    </SectionCard>

                    <SectionCard
                        icon={MapPin}
                        title="Address Information"
                        testId="company-add-section-address-information"
                        titleTestId="company-add-section-address-information-title"
                    >
                    <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Address Line1 <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    data-testid="company-add-address-line1-input"
                                    type="text"
                                    {...register("address1")}
                                    className={`${styles.customFormControl} ${errors.address1 ? '!border-red-500' : ''}`}
                                />
                                {errors.address1 && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.address1.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Address Line2</label>
                                <input
                                    data-testid="company-add-address-line2-input"
                                    type="text"
                                    {...register("address2")}
                                    className={`${styles.customFormControl} ${errors.address2 ? '!border-red-500' : ''}`}
                                />
                                {errors.address2 && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.address2.message}
                                    </div>
                                )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Company City <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    data-testid="company-add-city-input"
                                    type="text"
                                    {...register("city")}
                                    className={`${styles.customFormControl} ${errors.city ? '!border-red-500' : ''}`}
                                />
                                {errors.city && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.city.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Select Company State <span className={styles.requiredStar}>*</span>
                                </label>
                                <div className={styles.selectWrap}>
                                <select
                                    data-testid="company-add-state-select"
                                    {...register("state")}
                                    className={`${styles.customFormControl} ${errors.state ? '!border-red-500' : ''}`}
                                >
                                    <option value="">Select State</option>
                                    {states.map((s) => (
                                        <option key={s.state_code} value={s.state_code}>
                                            {s.state_name}
                                        </option>
                                    ))}
                                </select>
                                </div>
                                {errors.state && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.state.message}
                                    </div>
                                )}
                        </div>

                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Company Zipcode <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    data-testid="company-add-zipcode-input"
                                    type="text"
                                    {...register("zipcode")}
                                    className={`${styles.customFormControl} ${errors.zipcode ? '!border-red-500' : ''}`}
                                />
                                {errors.zipcode && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.zipcode.message}
                                    </div>
                                )}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 pt-1">
                            <button
                                data-testid="company-add-validate-address-button"
                                type="button"
                                className={styles.btnPrimary}
                                onClick={handleValidateAddress}
                                disabled={isValidatingAddress}
                            >
                                {isValidatingAddress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Validate Address
                            </button>
                    </div>
                    </div>
                    </SectionCard>

                    <SectionCard
                        icon={BadgeCheck}
                        title="Liberty Reference"
                        testId="company-add-section-liberty-reference"
                        titleTestId="company-add-section-liberty-reference-title"
                    >
                        <div
                            className="max-w-xs"
                            style={{ opacity: !isLibertyRole ? 0.5 : 1, pointerEvents: !isLibertyRole ? 'none' : 'auto' }}
                        >
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Active?<span className={styles.requiredStar}>*</span>
                                </label>
                                <div className={styles.selectWrap}>
                                <select
                                    data-testid="company-add-listed-select"
                                    {...register("listed")}
                                    className={styles.customFormControl}
                                >
                                    <option value="">Select Reference</option>
                                    <option value="1">Active</option>
                                    <option value="0">Not Active</option>
                                </select>
                                </div>
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard
                        icon={CalendarClock}
                        title="Liberty Report Email Frequency"
                        testId="company-add-section-email-frequency"
                        titleTestId="company-add-section-email-frequency-title"
                    >
                        <div className="max-w-xs sm:max-w-sm">
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Report email cadence<span className={styles.requiredStar}>*</span>
                                </label>
                                <div className={styles.selectWrap}>
                                    <select
                                        data-testid="company-add-email-frequency-select"
                                        {...register("email_frequency")}
                                        className={`${styles.customFormControl} ${errors.email_frequency ? '!border-red-500' : ''}`}
                                    >
                                        <option value="">Select Frequency</option>
                                        <option value="WEEKLY">Weekly</option>
                                        <option value="MONTHLY">Monthly</option>
                                        <option value="NONE">None</option>
                                    </select>
                                </div>
                                {errors.email_frequency && (
                                    <div className={styles.errorMessage}>
                                        <AlertCircle className="h-4 w-4" /> {errors.email_frequency.message}
                                    </div>
                                )}
                            </div>
                        </div>
                    </SectionCard>

                    <SectionCard
                        icon={Map}
                        title="Location Map"
                        testId="company-add-section-location-map"
                        titleTestId="company-add-section-location-map-title"
                    >
                        <input
                            type="hidden"
                            data-testid="company-add-latitude-input"
                            {...register("latitude")}
                        />
                        <input
                            type="hidden"
                            data-testid="company-add-longitude-input"
                            {...register("longitude")}
                        />
                        <div className="flex flex-wrap items-end gap-6">
                            <button
                                data-testid="company-add-refresh-map-button"
                                type="button"
                                className={styles.btnPrimary}
                                onClick={handleValidateAddress}
                                disabled={isValidatingAddress}
                            >
                                {isValidatingAddress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Refresh Map
                            </button>
                        </div>
                        <div
                            className="mt-4 overflow-hidden rounded-lg border border-slate-200"
                            style={{ width: "100%", height: "400px" }}
                        >
                            <div
                                data-testid="company-add-map"
                                id="map"
                                ref={mapRef}
                                className="h-full w-full"
                            />
                        </div>
                    </SectionCard>

                    <SectionCard
                        icon={Building2}
                        title="Property Mgmt System"
                        testId="company-add-section-property-mgmt"
                        titleTestId="company-add-section-property-mgmt-title"
                    >
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Property Mgmt System</label>
                                <Controller
                                    name="property_mgmt_system"
                                    control={control}
                                    render={({ field }) => (
                                        <SearchableSelect
                                            triggerDataTestId="company-add-property-mgmt-system-trigger"
                                            options={propertyMgmtSystems}
                                            value={field.value}
                                            onChange={field.onChange}
                                            multiple={true}
                                            selectedChipsLayout="flex-wrap"
                                            placeholder="Property Mgmt System"
                                            className={styles.customFormControl}
                                        />
                                    )}
                                />
                        </div>
                        <div className={styles.formGroup}>
                                {watchPropertyMgmt?.includes('Other') && (
                                    <>
                                        <label className={styles.formLabel}>
                                            Other<span className={styles.requiredStar}>*</span>
                                        </label>
                                        <input
                                            data-testid="company-add-property-mgmt-other-input"
                                            type="text"
                                            {...register("property_mgmt_system_other")}
                                            placeholder="Other"
                                            className={`${styles.customFormControl} ${errors.property_mgmt_system_other ? '!border-red-500' : ''}`}
                                        />
                                        {errors.property_mgmt_system_other && (
                                            <div className={styles.errorMessage}>
                                                <AlertCircle className="h-4 w-4" /> {errors.property_mgmt_system_other.message}
                                            </div>
                                        )}
                                    </>
                                )}
                        </div>
                        </div>
                    </SectionCard>

                    <SectionCard
                        icon={ShieldCheck}
                        title="Resident Screening"
                        testId="company-add-section-resident-screening"
                        titleTestId="company-add-section-resident-screening-title"
                    >
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Resident Screening</label>
                                <Controller
                                    name="resident_screening"
                                    control={control}
                                    render={({ field }) => (
                                        <SearchableSelect
                                            triggerDataTestId="company-add-resident-screening-trigger"
                                            options={residentScreenings}
                                            value={field.value}
                                            onChange={field.onChange}
                                            multiple={true}
                                            selectedChipsLayout="flex-wrap"
                                            placeholder="Resident Screening"
                                            className={styles.customFormControl}
                                        />
                                    )}
                                />
                        </div>
                        <div className={styles.formGroup}>
                                {watchResidentScreening?.includes('Other') && (
                                    <>
                                        <label className={styles.formLabel}>
                                            Other<span className={styles.requiredStar}>*</span>
                                        </label>
                                        <input
                                            data-testid="company-add-resident-screening-other-input"
                                            type="text"
                                            {...register("resident_screening_other")}
                                            placeholder="Other"
                                            className={`${styles.customFormControl} ${errors.resident_screening_other ? '!border-red-500' : ''}`}
                                        />
                                        {errors.resident_screening_other && (
                                            <div className={styles.errorMessage}>
                                                <AlertCircle className="h-4 w-4" /> {errors.resident_screening_other.message}
                                            </div>
                                        )}
                                    </>
                                )}
                        </div>
                        </div>
                    </SectionCard>

                    <div className="flex flex-wrap gap-3 pt-2">
                            <button
                                data-testid="company-add-submit-button"
                                type="submit"
                                disabled={isSubmitting}
                                className={cn(styles.btnPrimary, "min-w-[150px]")}
                            >
                                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Submit
                            </button>
                            <button
                                data-testid="company-add-cancel-button"
                                type="button"
                                onClick={() => navigate('/company')}
                                disabled={isSubmitting}
                                className={cn(
                                    styles.applyButton,
                                    styles.cancelButtonSecondary,
                                    "min-w-[150px]",
                                )}
                            >
                                Cancel
                            </button>
                    </div>

                </form>
            </div>

            <ValidationModal
                isOpen={showValidationModal}
                title={validationModalTitle ?? (validationModalVariant === 'success' ? 'Verification Success' : 'Verify Address')}
                message={validationModalMessage}
                variant={validationModalVariant}
                showValidationIcon={
                    validationModalTitle === 'Validate Address' ||
                    validationModalTitle === 'Missing Fields' ||
                    validationModalTitle === 'Validation Message'
                }
                onClose={() => setShowValidationModal(false)}
                alignMessage
                okButtonStyle="saveChanges"
                overlayDataTestId="company-add-validation-modal-overlay"
                closeButtonDataTestId="company-add-validation-modal-close-button"
                confirmButtonDataTestId="company-add-validation-modal-ok-button"
            />

            <ConfirmDialog
                isOpen={duplicateAddressConfirmOpen}
                title="Another company exists at this address"
                message=""
                htmlMessage={duplicateAddressHtmlMessage}
                primaryLabel="Save"
                onPrimary={handleDuplicateCompanyAddressSave}
                secondaryLabel="Cancel"
                onSecondary={handleDuplicateCompanyAddressCancel}
                onDismiss={handleDuplicateCompanyAddressCancel}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-add-duplicate-address-confirm-overlay"
                primaryButtonDataTestId="company-add-duplicate-address-confirm-save-button"
                secondaryButtonDataTestId="company-add-duplicate-address-confirm-cancel-button"
                closeButtonDataTestId="company-add-duplicate-address-confirm-close-button"
            />
        </div>
    );
}
